import { CustomError } from '@/utilities/errors/CustomError';
import { getProviderRef } from '@/utilities/getters/gameContextGetters';

import { APIServiceType } from '@/api/types';
import { ErrorType } from '@/utilities/errors/types';
import { GameWindowData, GameHelpData, ExtendedGameInfo } from '../types';
import { useStore } from '@/store/useStore';
import {
  setCommonUIContext,
  setGameContext,
  setGameStatus,
} from '@/store/actions';
import { resolveLaunchGeoProduct } from '@/geolocation/resolve-launch-geo-product';

const dispatchGameContext = (aggregationData: {
  gameWindowData?: GameWindowData;
  gameHelpData?: GameHelpData;
  gameInfo?: ExtendedGameInfo;
}) => {
  const { gameUid } = aggregationData.gameInfo ?? {};

  if (
    !aggregationData.gameInfo ||
    Object.keys(aggregationData.gameInfo).length === 0 ||
    !aggregationData.gameInfo?.gameUid
  ) {
    throw new CustomError('[DATA_FETCH] Missing gameInfo or gameUid', {
      service: APIServiceType.AGGREGATION,
      type: ErrorType.API,
    });
  }

  const providerRef = getProviderRef(aggregationData.gameInfo);
  const { region, product, wrapperType } = useStore.getState().runtime;
  const providerName =
    aggregationData.gameInfo?.provider?.providerName?.toUpperCase();

  setGameContext({
    ...(process.env.BRAND && { brand: process.env.BRAND?.toLowerCase() }),
    ...(aggregationData.gameInfo?.rgi && {
      rgi: aggregationData.gameInfo?.rgi?.toLowerCase(),
    }),
    ...(aggregationData.gameInfo?.provider?.providerName && {
      provider: aggregationData.gameInfo?.provider?.providerName?.toUpperCase(),
    }),
    showNetPosition:
      aggregationData.gameInfo?.provider?.show_net_position ?? false,
    unpausable: aggregationData.gameInfo?.unpausable ?? false,
    superclass: aggregationData.gameInfo?.superclass?.uid ?? null,
    gameStudio: aggregationData.gameInfo?.game_studio?.uid ?? null,
    ...((providerRef || aggregationData.gameInfo?.rgs_code_desktop) && {
      providerRef: providerRef ?? aggregationData.gameInfo?.rgs_code_desktop,
    }),
    ...((aggregationData.gameInfo?.seo_game_description_headline ||
      aggregationData.gameInfo?.display_name?.[0]?.text) && {
      gameName:
        aggregationData.gameInfo?.seo_game_description_headline ??
        aggregationData.gameInfo?.display_name?.[0]?.text,
    }),
    region,
    gameId: aggregationData.gameInfo?.documentId || gameUid,
    gameUid,
    launchGeoProduct: resolveLaunchGeoProduct({
      runtimeProduct: product,
      providerName,
      isAndroid: wrapperType.isAndroid,
    }),
  });

  setGameStatus({
    hiddenTestingFlags:
      aggregationData.gameInfo?.feature_keys?.reduce((acc, key) => {
        if (
          key?.feature_key?.uid &&
          !acc.includes(key?.feature_key?.uid ?? '')
        ) {
          acc.push(key?.feature_key?.uid.split('_')[0]);
        }
        return acc;
      }, [] as Array<string>) ?? null,
    overallStatus: aggregationData.gameInfo?.status_overall ?? null,
  });

  setCommonUIContext({
    depositLink: aggregationData.gameWindowData?.primary?.deposit_link?.url,
    supportLink: aggregationData.gameWindowData?.primary?.support_link?.url,
    depositLimitsLink:
      aggregationData.gameWindowData?.primary?.set_deposit_limits_link?.url,
    responsibleGamingLink:
      aggregationData.gameWindowData?.primary?.responsible_gaming_link?.url,
    showGameWindow: aggregationData.gameWindowData?.primary?.show_game_window,
    showDepositButton:
      aggregationData.gameWindowData?.primary?.show_deposit_button,
    showExitButton: aggregationData.gameWindowData?.primary?.show_exit_button,
    showSoundButton: aggregationData.gameWindowData?.primary?.show_sound_button,
    showLogo: aggregationData.gameWindowData?.primary?.show_logo,
    showResponsibleGaming:
      aggregationData.gameWindowData?.primary?.show_responsible_gaming,
    themeData: aggregationData.gameInfo?.game_loading_screen || undefined,
    gameHelpItems: aggregationData.gameHelpData?.items ?? [],
  });
};

export default dispatchGameContext;
